'use client';

import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, Loader2 } from 'lucide-react';

interface Il {
  id: number;
  name: string;
}

interface Ilce {
  id: number;
  name: string;
}

interface Mahalle {
  id: number;
  name: string;
}

interface Props {
  il: string;
  ilce: string;
  mahalle: string;
  onIlChange: (il: string) => void;
  onIlceChange: (ilce: string) => void;
  onMahalleChange: (mahalle: string) => void;
}

// Cache
let illerCache: Il[] | null = null;
const ilcelerCache: Record<number, Ilce[]> = {};
const mahallelerCache: Record<number, Mahalle[]> = {};


function slugify(str: string): string {
  return str
    .toLocaleLowerCase('tr')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/Ü/g, 'u')
    .replace(/ş/g, 's').replace(/Ş/g, 's')
    .replace(/ı/g, 'i').replace(/I/g, 'i')
    .replace(/ö/g, 'o').replace(/Ö/g, 'o')
    .replace(/ç/g, 'c').replace(/Ç/g, 'c')
    .replace(/İ/g, 'i');
}

function SearchableDropdown({
  value,
  options,
  placeholder,
  onChange,
  disabled,
  yukleniyor,
}: {
  value: string;
  options: { id: number; name: string }[];
  placeholder: string;
  onChange: (val: string) => void;
  disabled?: boolean;
  yukleniyor?: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const [arama, setArama] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const aramaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setAcik(false);
        setArama('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (acik && aramaRef.current) {
      setTimeout(() => aramaRef.current?.focus(), 50);
    }
  }, [acik]);

  const filtrelenmis = options.filter(o =>
    slugify(o.name).includes(slugify(arama))
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled || yukleniyor}
        onClick={() => { setAcik(!acik); setArama(''); }}
        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-[var(--radius-md)] text-sm transition ${
          disabled || yukleniyor
            ? 'bg-[var(--color-gray-50)] text-[var(--color-gray-400)] cursor-not-allowed border-[var(--color-gray-200)]'
            : 'bg-white text-[var(--color-gray-800)] border-[var(--color-gray-300)] hover:border-[var(--color-primary)] cursor-pointer'
        }`}
      >
        <span className={value ? 'text-[var(--color-gray-800)]' : 'text-[var(--color-gray-400)]'}>
          {value || placeholder}
        </span>
        {yukleniyor
          ? <Loader2 className="w-4 h-4 animate-spin text-[var(--color-gray-400)]" />
          : <ChevronDown className={`w-4 h-4 text-[var(--color-gray-400)] transition-transform ${acik ? 'rotate-180' : ''}`} />
        }
      </button>

      {acik && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[var(--color-gray-200)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] z-50">
          {/* Arama */}
          <div className="p-2 border-b border-[var(--color-gray-100)]">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-[var(--color-gray-50)] rounded-[var(--radius-sm)]">
              <Search className="w-3.5 h-3.5 text-[var(--color-gray-400)]" />
              <input
                ref={aramaRef}
                type="text"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Ara..."
                className="flex-1 text-sm bg-transparent outline-none text-[var(--color-gray-700)]"
              />
            </div>
          </div>
          {/* Seçenekler */}
          <div className="max-h-48 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {filtrelenmis.length === 0 ? (
              <div className="px-3 py-4 text-sm text-center text-[var(--color-gray-400)]">Sonuç bulunamadı</div>
            ) : (
              filtrelenmis.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); onChange(o.name); setAcik(false); setArama(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--color-gray-50)] transition ${
                    value === o.name ? 'text-[var(--color-primary)] font-medium bg-[var(--color-info-light)]' : 'text-[var(--color-gray-700)]'
                  }`}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function IlIlceSecici({ il, ilce, mahalle, onIlChange, onIlceChange, onMahalleChange }: Props) {
  const [iller, setIller] = useState<Il[]>(illerCache ?? []);
  const [ilceler, setIlceler] = useState<Ilce[]>([]);
  const [mahalleler, setMahalleler] = useState<Mahalle[]>([]);
  const [ilYukleniyor, setIlYukleniyor] = useState(!illerCache);
  const [ilceYukleniyor, setIlceYukleniyor] = useState(false);
  const [mahalleYukleniyor, setMahalleYukleniyor] = useState(false);
  const [ilTetikle, setIlTetikle] = useState(0);
  const [ilceTetikle, setIlceTetikle] = useState(0);

  // İlleri yükle
  useEffect(() => {
    if (illerCache) { setIller(illerCache); setIlYukleniyor(false); return; }
    fetch('https://api.turkiyeapi.dev/v1/provinces?fields=id,name&limit=100')
      .then(r => r.json())
      .then(data => {
        const liste = (data.data ?? []).sort((a: Il, b: Il) => a.name.localeCompare(b.name, 'tr'));
        illerCache = liste;
        setIller(liste);
      })
      .catch(() => {})
      .finally(() => setIlYukleniyor(false));
  }, []);

  // İl değişince ilçeleri yükle
  useEffect(() => {
    if (!il) { setIlceler([]); setMahalleler([]); return; }
    const seciliIl = iller.find(i => i.name === il);
    if (!seciliIl) return;

    if (ilcelerCache[seciliIl.id]) {
      setIlceler(ilcelerCache[seciliIl.id]);
      return;
    }

    setIlceYukleniyor(true);
    fetch(`https://api.turkiyeapi.dev/v1/districts?provinceId=${seciliIl.id}&fields=id,name&limit=200`)
      .then(r => r.json())
      .then(data => {
        const liste = (data.data ?? []).sort((a: Ilce, b: Ilce) => a.name.localeCompare(b.name, 'tr'));
        ilcelerCache[seciliIl.id] = liste;
        setIlceler(liste);
      })
      .catch(() => {})
      .finally(() => setIlceYukleniyor(false));
  }, [il, iller, ilTetikle]);

  // İlçe değişince mahalleleri yükle
  useEffect(() => {
    if (!ilce) { setMahalleler([]); return; }
    const seciliIlce = ilceler.find(i => i.name === ilce);
    if (!seciliIlce) return;

    if (mahallelerCache[seciliIlce.id]) {
      setMahalleler(mahallelerCache[seciliIlce.id]);
      return;
    }

    setMahalleYukleniyor(true);
    fetch(`https://api.turkiyeapi.dev/v1/neighborhoods?districtId=${seciliIlce.id}&fields=id,name&limit=500`)
      .then(r => r.json())
      .then(data => {
        const liste = (data.data ?? []).sort((a: Mahalle, b: Mahalle) => a.name.localeCompare(b.name, 'tr'));
        mahallelerCache[seciliIlce.id] = liste;
        setMahalleler(liste);
      })
      .catch(() => {})
      .finally(() => setMahalleYukleniyor(false));
  }, [ilce, ilceler, ilceTetikle]);

  const ilDegisti = (yeniIl: string) => {
    onIlChange(yeniIl);
    onIlceChange('');
    onMahalleChange('');
    setIlceler([]);
    setMahalleler([]);
    setIlTetikle(t => t + 1);
  };

  const ilceDegisti = (yeniIlce: string) => {
    onIlceChange(yeniIlce);
    onMahalleChange('');
    setMahalleler([]);
    setIlceTetikle(t => t + 1);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <label className="block text-sm font-medium text-[var(--color-gray-700)] mb-1.5">
          İl <span className="text-[var(--color-error)]">*</span>
        </label>
        <SearchableDropdown
          value={il}
          options={iller}
          placeholder="İl seçin"
          onChange={ilDegisti}
          yukleniyor={ilYukleniyor}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-gray-700)] mb-1.5">
          İlçe <span className="text-[var(--color-error)]">*</span>
        </label>
        <SearchableDropdown
          value={ilce}
          options={ilceler}
          placeholder={il ? 'İlçe seçin' : 'Önce il seçin'}
          onChange={ilceDegisti}
          disabled={!il || ilceYukleniyor}
          yukleniyor={ilceYukleniyor}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-gray-700)] mb-1.5">
          Mahalle
        </label>
        <SearchableDropdown
          value={mahalle}
          options={mahalleler}
          placeholder={ilce ? 'Mahalle seçin' : 'Önce ilçe seçin'}
          onChange={onMahalleChange}
          disabled={!ilce || mahalleYukleniyor}
          yukleniyor={mahalleYukleniyor}
        />
      </div>
    </div>
  );
}
